/**
 * Lectura de pedidos POS/mesas/ecommerce desde el dominio único `orders_v2`.
 *
 * Reconstruye la forma plana que antes exponía la vista compat `orders`
 * (espeja su DDL: muchos campos de POS/mesas viven en `orders_v2.source_metadata`
 * jsonb; `total`=grand_total; `order_type` deriva de `channel`; `status` de
 * `source_metadata.legacy_status` con fallback al `lifecycle_stage`), y la de
 * `order_items` desde `order_lines`.
 *
 * Shim de LECTURA interino y borrable: cuando cada consumidor pase a la forma
 * nativa de orders_v2 se elimina. Lee tablas reales → habilita el drop de las vistas.
 */

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | null => (v == null ? null : String(v));

function mapLifecycleToLegacyStatus(stage: string | null | undefined): string {
  switch (stage) {
    case 'quotation': return 'pending';
    case 'sale': return 'confirmed';
    case 'fulfilled': return 'delivered';
    case 'closed': return 'delivered';
    case 'cancelled': return 'cancelled';
    default: return 'pending';
  }
}

interface OrdersV2Row {
  id: string;
  tenant_id?: string | null;
  branch_id?: string | null;
  order_number?: string | null;
  lifecycle_stage?: string | null;
  channel?: string | null;
  is_cod?: boolean | null;
  subtotal?: number | null;
  tax_total?: number | null;
  grand_total?: number | null;
  discount_total?: number | null;
  shipping_total?: number | null;
  payment_status?: string | null;
  notes?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  source_metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  order_delivery_profile?: { address?: string | null } | null;
}

/** Select que trae todo lo necesario para reconstruir la forma legacy de `orders`. */
export const ORDER_V2_FULL_SELECT =
  'id, tenant_id, branch_id, order_number, lifecycle_stage, channel, is_cod, subtotal, tax_total, grand_total, discount_total, shipping_total, payment_status, notes, customer_name, customer_phone, customer_email, source_metadata, created_at, updated_at, order_delivery_profile(address)';

/** Forma plana equivalente a la antigua vista `orders`. */
export interface OrderFlat {
  id: string;
  tenant_id: string | null;
  branch_id: string | null;
  order_number: number | null;
  table_number: string | null;
  table_qr_id: string | null;
  status: string;
  subtotal: number | null;
  tax: number;
  total: number | null;
  payment_method: string | null;
  payment_status: string | null;
  waiter_name: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  delivery_method: string | null;
  delivery_address: string | null;
  order_type: string | null;
  delivery_zone_id: string | null;
  delivery_fee_original: number;
  delivery_fee_restaurant: number;
  delivery_fee_customer: number;
  delivery_rule_applied_id: string | null;
  courier_service_id: string | null;
  payment_provider_code: string | null;
  payment_transaction_id: string | null;
  gateway_reference: string | null;
  discount_code: string | null;
  discount_code_id: string | null;
  discount_amount: number | null;
}

/** Mapea una fila de orders_v2 (con source_metadata) a la forma plana legacy de `orders`. */
export function mapOrderV2Row(row: unknown): OrderFlat {
  const r = (row ?? {}) as OrdersV2Row;
  const sm = (r.source_metadata ?? {}) as Record<string, unknown>;
  const digits = String(r.order_number ?? '').replace(/[^0-9]/g, '');
  return {
    id: r.id,
    tenant_id: r.tenant_id ?? null,
    branch_id: r.branch_id ?? null,
    order_number: digits === '' ? null : Number(digits),
    table_number: str(sm.table_number),
    table_qr_id: str(sm.table_qr_id),
    status: str(sm.legacy_status) ?? mapLifecycleToLegacyStatus(r.lifecycle_stage),
    subtotal: r.subtotal ?? null,
    tax: num(sm.tax) ?? r.tax_total ?? 0,
    total: r.grand_total ?? null,
    payment_method: str(sm.payment_method),
    payment_status: r.payment_status === 'unpaid' ? 'pending' : (r.payment_status ?? null),
    waiter_name: str(sm.waiter_name),
    notes: r.notes ?? null,
    created_at: r.created_at ?? null,
    updated_at: r.updated_at ?? null,
    customer_name: r.customer_name ?? null,
    customer_phone: r.customer_phone ?? null,
    customer_email: r.customer_email ?? null,
    delivery_method: str(sm.delivery_method) ?? (r.is_cod ? 'delivery' : null),
    delivery_address: str(sm.delivery_address) ?? (r.order_delivery_profile?.address ?? null),
    order_type: r.channel === 'online_checkout' ? 'ecommerce' : (r.channel ?? null),
    delivery_zone_id: str(sm.delivery_zone_id),
    delivery_fee_original: num(sm.delivery_fee_original) ?? 0,
    delivery_fee_restaurant: num(sm.delivery_fee_restaurant) ?? 0,
    delivery_fee_customer: num(sm.delivery_fee_customer) ?? r.shipping_total ?? 0,
    delivery_rule_applied_id: str(sm.delivery_rule_applied_id),
    courier_service_id: str(sm.courier_service_id),
    payment_provider_code: str(sm.payment_provider_code),
    payment_transaction_id: str(sm.payment_transaction_id),
    gateway_reference: str(sm.gateway_reference),
    discount_code: str(sm.discount_code),
    discount_code_id: str(sm.discount_code_id),
    discount_amount: r.discount_total ?? null,
  };
}

export function mapOrderV2Rows(rows: unknown): OrderFlat[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(mapOrderV2Row);
}

interface OrderLineRow {
  id?: string;
  order_id?: string | null;
  product_id?: string | null;
  name?: string | null;
  qty?: number | null;
  unit_price?: number | null;
  modifiers?: unknown;
  variant?: unknown;
  line_total?: number | null;
  created_at?: string | null;
  notes?: string | null;
}

/** Forma plana equivalente a la antigua vista `order_items`. */
export interface OrderItemFlat {
  id: string | undefined;
  order_id: string | null;
  product_id: string | null;
  product_name: string | null;
  quantity: number;
  unit_price: number | null;
  modifiers: unknown;
  variant: unknown;
  subtotal: number | null;
  created_at: string | null;
  notes: string | null;
}

/** Select de order_lines con los campos de la forma legacy de order_items. */
export const ORDER_LINES_ITEM_SELECT =
  'id, order_id, product_id, name, qty, unit_price, modifiers, variant, line_total, created_at, notes';

/** Mapea una fila de order_lines a la forma plana legacy de `order_items`. */
export function mapOrderLineToItem(row: unknown): OrderItemFlat {
  const l = (row ?? {}) as OrderLineRow;
  return {
    id: l.id,
    order_id: l.order_id ?? null,
    product_id: l.product_id ?? null,
    product_name: l.name ?? null,
    quantity: Math.round(Number(l.qty ?? 0)),
    unit_price: l.unit_price ?? null,
    modifiers: Array.isArray(l.modifiers) ? l.modifiers : [],
    variant: l.variant ?? null,
    subtotal: l.line_total ?? null,
    created_at: l.created_at ?? null,
    notes: l.notes ?? null,
  };
}

export function mapOrderLinesToItems(rows: unknown): OrderItemFlat[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(mapOrderLineToItem);
}
